import type {
  WhatsAppAllowancePolicy,
  WhatsAppOperatorExposureView,
  WhatsAppOperatorReadiness,
} from "@reminder-tracker/backend-contracts";
import { useMutation, useQuery } from "convex/react";
import { useRef, useState } from "react";

import { useAdministratorAuthentication } from "../../auth/authentication-context";
import {
  configureWhatsAppAllowancePolicy,
  configureWhatsAppOperatorPolicy,
  getWhatsAppAllowancePolicy,
  getWhatsAppOperatorExposure,
  getWhatsAppOperatorReadiness,
} from "../../backend/functions";

type Feedback = Readonly<{ kind: "error" | "success"; message: string }>;

function opaqueKey(prefix: "cmd" | "corr"): string {
  if (typeof globalThis.crypto.randomUUID !== "function")
    throw new Error("A secure administration identifier is unavailable.");
  return `${prefix}_${globalThis.crypto.randomUUID().replaceAll("-", "")}`;
}

function pounds(microGbp: number | null): string {
  return microGbp === null ? "Not configured" : `£${(microGbp / 1_000_000).toFixed(2)}`;
}

export function ProtectedWhatsAppGovernance(): React.JSX.Element | null {
  const { authentication } = useAdministratorAuthentication();
  if (authentication.state !== "authorized") return null;
  const canManagePolicy = authentication.permissions.includes("delivery:manage-policy");
  const canManageAllowance = authentication.permissions.includes("messaging:manage-allowance");
  return (
    <ConnectedWhatsAppGovernance
      canManagePolicy={canManagePolicy}
      canManageAllowance={canManageAllowance}
    />
  );
}

function ConnectedWhatsAppGovernance({
  canManagePolicy,
  canManageAllowance,
}: Readonly<{ canManagePolicy: boolean; canManageAllowance: boolean }>): React.JSX.Element {
  const [correlationId] = useState(() => opaqueKey("corr"));
  const readiness = useQuery(getWhatsAppOperatorReadiness, { correlationId });
  const exposure = useQuery(getWhatsAppOperatorExposure, { correlationId });
  const allowance = useQuery(
    getWhatsAppAllowancePolicy,
    canManageAllowance ? { correlationId } : "skip",
  );
  const configureOperator = useMutation(configureWhatsAppOperatorPolicy);
  const configureAllowance = useMutation(configureWhatsAppAllowancePolicy);
  const disableCommand = useRef<string | null>(null);
  const allowanceCommand = useRef<string | null>(null);
  const [freeUnits, setFreeUnits] = useState("");
  const [working, setWorking] = useState<"allowance" | "disable" | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const disable = async (): Promise<void> => {
    if (readiness === undefined || working !== null) return;
    disableCommand.current ??= opaqueKey("cmd");
    setWorking("disable");
    setFeedback(null);
    try {
      const result = await configureOperator({
        mode: "disabled",
        limits: null,
        maximumMicroGbp: null,
        markets: [],
        reason: "incident-containment",
        expectedRevision: readiness.policy?.revision ?? null,
        commandKey: disableCommand.current,
        correlationId: opaqueKey("corr"),
      });
      disableCommand.current = null;
      setFeedback({
        kind: "success",
        message: `WhatsApp sending is Disabled at policy revision ${result.policy.revision}.`,
      });
    } catch {
      setFeedback({
        kind: "error",
        message: "The sending policy was not confirmed. Refresh before retrying.",
      });
    } finally {
      setWorking(null);
    }
  };

  const publishAllowance = async (): Promise<void> => {
    if (working !== null || !/^\d{1,6}$/u.test(freeUnits)) return;
    allowanceCommand.current ??= opaqueKey("cmd");
    setWorking("allowance");
    setFeedback(null);
    try {
      const result = await configureAllowance({
        expectedRevision: allowance?.revision ?? null,
        freeUnits: Number(freeUnits),
        effectiveAt: Date.now() + 60_000,
        subscriptionsAvailable: false,
        topUpsAvailable: false,
        commandKey: allowanceCommand.current,
        correlationId: opaqueKey("corr"),
      });
      allowanceCommand.current = null;
      setFeedback({
        kind: "success",
        message: `Free allowance policy revision ${result.revision} starts in about one minute.`,
      });
      setFreeUnits("");
    } catch {
      setFeedback({
        kind: "error",
        message: "The free allowance was not published. Refresh and review the current policy.",
      });
    } finally {
      setWorking(null);
    }
  };

  return (
    <WhatsAppGovernanceView
      readiness={readiness}
      exposure={exposure}
      allowance={allowance}
      canManagePolicy={canManagePolicy}
      canManageAllowance={canManageAllowance}
      freeUnits={freeUnits}
      working={working}
      feedback={feedback}
      onFreeUnitsChange={setFreeUnits}
      onDisable={() => void disable()}
      onPublishAllowance={() => void publishAllowance()}
    />
  );
}

export function WhatsAppGovernanceView({
  readiness,
  exposure,
  allowance,
  canManagePolicy,
  canManageAllowance,
  freeUnits,
  working,
  feedback,
  onFreeUnitsChange,
  onDisable,
  onPublishAllowance,
}: Readonly<{
  readiness: WhatsAppOperatorReadiness | undefined;
  exposure: WhatsAppOperatorExposureView | undefined;
  allowance: WhatsAppAllowancePolicy | null | undefined;
  canManagePolicy: boolean;
  canManageAllowance: boolean;
  freeUnits: string;
  working: "allowance" | "disable" | null;
  feedback: Feedback | null;
  onFreeUnitsChange: (value: string) => void;
  onDisable: () => void;
  onPublishAllowance: () => void;
}>): React.JSX.Element {
  if (readiness === undefined || exposure === undefined) {
    return (
      <section
        className="push-governance push-governance-state"
        aria-busy="true"
        aria-live="polite"
      >
        <h2>WhatsApp governance</h2>
        <p>Reading privacy-safe WhatsApp readiness…</p>
      </section>
    );
  }
  const disabled = readiness.policy?.mode === "disabled";
  return (
    <section className="push-governance" aria-labelledby="whatsapp-governance-title">
      <div className="section-head push-governance-head">
        <div>
          <h2 id="whatsapp-governance-title">WhatsApp governance</h2>
          <p>
            Review readiness, conserved operator exposure and the free group allowance. No phone
            number or reminder content is available here.
          </p>
        </div>
        <span className="push-environment">{readiness.environment}</span>
      </div>

      <div className="push-policy">
        <div>
          <span className="push-policy-label">Current sending policy</span>
          <strong>
            {readiness.policy === null
              ? "Not configured"
              : readiness.policy.mode === "disabled"
                ? "Disabled"
                : "Test only"}
          </strong>
          <small>
            External sending:{" "}
            {readiness.externalSendAuthorization === "authorized"
              ? "Authorised for the recorded test window"
              : "Not authorised"}
          </small>
        </div>
        {canManagePolicy ? (
          <button type="button" disabled={working !== null || disabled} onClick={onDisable}>
            {working === "disable" ? "Recording…" : "Set Disabled"}
          </button>
        ) : null}
      </div>

      <div className="push-aggregate-groups">
        <Aggregate
          title="Readiness"
          values={[
            ["Provider", "Meta Cloud API"],
            ["Language", "English"],
            [
              "Provider verification",
              readiness.providerVerification === "verified" ? "Verified" : "Not verified",
            ],
            [
              "Template verification",
              readiness.templateVerification === "verified" ? "Verified" : "Not verified",
            ],
          ]}
        />
        <Aggregate
          title="Operator exposure"
          values={[
            ["Reserved", pounds(exposure.reservedMicroGbp)],
            ["Settled", pounds(exposure.settledMicroGbp)],
            ["In flight", String(exposure.inFlight)],
            ["Headroom", pounds(exposure.headroomMicroGbp)],
          ]}
        />
      </div>

      <div className="push-outcome-note">
        <strong>Readiness blockers</strong>
        {readiness.blockers.length === 0 ? (
          <p>No blockers recorded.</p>
        ) : (
          <ul>
            {readiness.blockers.map((blocker) => (
              <li key={blocker}>{blocker.replaceAll("-", " ")}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="push-policy">
        <div>
          <span className="push-policy-label">Monthly included messages</span>
          <strong>
            {allowance === undefined
              ? "Permission required"
              : allowance === null
                ? "Not configured"
                : allowance.freeUnits}
          </strong>
          <small>Paid plans and top-ups remain unavailable.</small>
        </div>
        {canManageAllowance ? (
          <div>
            <label htmlFor="whatsapp-free-units">New free allowance</label>{" "}
            <input
              id="whatsapp-free-units"
              inputMode="numeric"
              pattern="[0-9]*"
              value={freeUnits}
              onChange={(event) =>
                onFreeUnitsChange(event.target.value.replace(/\D/gu, "").slice(0, 6))
              }
            />
            <button
              type="button"
              disabled={working !== null || !/^\d{1,6}$/u.test(freeUnits)}
              onClick={onPublishAllowance}
            >
              {working === "allowance" ? "Publishing…" : "Publish free allowance"}
            </button>
          </div>
        ) : null}
      </div>

      {feedback === null ? null : (
        <p
          className={`push-feedback ${feedback.kind}`}
          role={feedback.kind === "error" ? "alert" : "status"}
        >
          {feedback.message}
        </p>
      )}
      <dl className="push-privacy-proof">
        <div>
          <dt>Phone numbers accessible</dt>
          <dd>No</dd>
        </div>
        <div>
          <dt>Reminder content accessible</dt>
          <dd>No</dd>
        </div>
      </dl>
    </section>
  );
}

function Aggregate({
  title,
  values,
}: Readonly<{ title: string; values: ReadonlyArray<readonly [string, string]> }>) {
  return (
    <section className="push-aggregate-group" aria-label={title}>
      <h3>{title}</h3>
      <dl>
        {values.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
